import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginContext } from '../../context/loginContext.js';
import { Spin, message, Typography, Button, Card, Tag, Space } from 'antd';
import axios from 'axios';

const { Text } = Typography;

// Interceptor de axios para loggear todas las requests
axios.interceptors.request.use(
  (config) => {
    console.log('🚀 [AXIOS REQUEST]', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL || ''}${config.url}`,
      data: config.data,
      headers: config.headers,
      timestamp: new Date().toISOString(),
    });
    return config;
  },
  (error) => {
    console.error('❌ [AXIOS REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// Interceptor de axios para loggear todas las responses
// Guardar el ID del interceptor para poder removerlo después
let responseInterceptorId = null;

// Función para configurar el interceptor que suprime errores 403 durante el login
const setupResponseInterceptor = () => {
  // Remover interceptor anterior si existe
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }
  
  responseInterceptorId = axios.interceptors.response.use(
    (response) => {
      console.log('✅ [AXIOS RESPONSE]', {
        status: response.status,
        statusText: response.statusText,
        url: response.config.url,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
      return response;
    },
    (error) => {
      // Verificar si estamos en proceso de autenticación
      const isProcessingAuth = sessionStorage.getItem('auth0Processing') === 'true' ||
                              sessionStorage.getItem('justLoggedIn') === 'true' || 
                              window.location.pathname.includes('/auth0-callback') ||
                              window.location.hash.includes('/auth0-callback');
      
      // Si es un error 403 y estamos procesando autenticación, suprimir el error
      if (error.response?.status === 403 && isProcessingAuth) {
        console.warn('⚠️ [AXIOS] Error 403 suprimido durante autenticación:', {
          url: error.config?.url,
          timestamp: new Date().toISOString(),
        });
        // Retornar una respuesta vacía en lugar de rechazar la promesa
        // Esto evita que se muestre el error al usuario
        return Promise.resolve({ 
          status: 403, 
          data: null, 
          config: error.config 
        });
      }
      
      console.error('❌ [AXIOS RESPONSE ERROR]', {
        message: error.message,
        code: error.code,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        } : null,
        request: error.request ? {
          url: error.config?.url,
          method: error.config?.method,
        } : null,
        timestamp: new Date().toISOString(),
      });
      return Promise.reject(error);
    }
  );
};

// El interceptor se configurará cuando el componente se monte

// Función helper para validar usuario (copiada del contexto)
function isValidUser(u) {
  if (!u || typeof u !== 'object') return false;
  const hasId = Number.isInteger(Number(u?.id)) || Number.isInteger(Number(u?.usu_id));
  const hasTipo = Number.isInteger(Number(u?.usu_tipousuario));
  return hasId && hasTipo;
}

const Auth0Callback = () => {
  const [searchParams] = useSearchParams();
  const { login } = useContext(LoginContext);
  const navigate = useNavigate();
  const [debugInfo, setDebugInfo] = useState({
    step: 'Iniciando...',
    data: null,
    error: null,
  });
  const [isProcessing, setIsProcessing] = useState(true);
  const [cuentasDisponibles, setCuentasDisponibles] = useState([]);
  const [auth0UserData, setAuth0UserData] = useState(null);
  const [seleccionandoCuenta, setSeleccionandoCuenta] = useState(false);
  const hasProcessedRef = useRef(false); // Evita doble ejecución en StrictMode
  
  // Configurar el interceptor al montar el componente y limpiarlo al desmontar
  useEffect(() => {
    // Marcar que estamos procesando autenticación desde el inicio
    sessionStorage.setItem('auth0Processing', 'true');
    
    // Configurar el interceptor si no está configurado
    if (responseInterceptorId === null) {
      setupResponseInterceptor();
    }
    
    return () => {
      // Limpiar el flag cuando el componente se desmonte
      sessionStorage.removeItem('auth0Processing');
      
      // Limpiar el interceptor después de un delay para permitir que se completen las solicitudes
      setTimeout(() => {
        if (responseInterceptorId !== null) {
          axios.interceptors.response.eject(responseInterceptorId);
          responseInterceptorId = null;
        }
      }, 3000);
    };
  }, []);

  // Log cuando el componente se monta
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🎯 [Auth0Callback] COMPONENTE MONTADO');
    console.log('   Timestamp:', new Date().toISOString());
    console.log('   window.location.href:', window.location.href);
    console.log('   window.location.pathname:', window.location.pathname);
    console.log('   window.location.search:', window.location.search);
    console.log('   window.location.hash:', window.location.hash);
    console.log('═══════════════════════════════════════════════════════════');
  }, []);

  const completarLoginConRespuesta = async (payload) => {
    const { usuario, token, refreshToken } = payload || {};
    if (!usuario || !token) {
      throw new Error('Respuesta de autenticación inválida');
    }

    const rol = (usuario.tiu_descripcion || '').toUpperCase();
    if (rol !== 'ALUMNO' && rol !== 'JUGADOR') {
      throw new Error('Tipo de cuenta no permitido');
    }

    const userData = {
      usu_id: usuario.usu_id,
      usu_usuario: usuario.usu_usuario,
      usu_tipousuario: usuario.usu_tipousuario,
      tiu_descripcion: usuario.tiu_descripcion,
      token,
      refreshToken,
      id: usuario.usu_id,
    };

    sessionStorage.setItem('justLoggedIn', 'true');
    if (refreshToken) {
      try {
        localStorage.setItem('refreshToken', refreshToken);
      } catch (err) {
        console.warn('No se pudo guardar refreshToken:', err);
      }
    }

    localStorage.setItem('user', JSON.stringify(userData));
    await new Promise((resolve) => setTimeout(resolve, 100));
    await login(userData);
    await new Promise((resolve) => setTimeout(resolve, 100));

    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!storedUser || !isValidUser(storedUser)) {
      throw new Error('No se pudo guardar la sesión');
    }

    const redirectTo = (storedUser.tiu_descripcion || '').toUpperCase() === 'JUGADOR'
      ? '/jugador/nuevo-turno'
      : '/home-alumno';

    setIsProcessing(false);
    sessionStorage.setItem('justLoggedIn', 'true');
    sessionStorage.removeItem('auth0Processing');
    setTimeout(() => {
      window.location.href = `/#${redirectTo}`;
    }, 300);
  };

  const handleSeleccionarCuenta = async (cuenta) => {
    if (!auth0UserData?.email || !auth0UserData?.sub || !cuenta) return;
    setSeleccionandoCuenta(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const response = await axios.post(
        `${apiUrl}/usuarios/auth0-login`,
        {
          email: auth0UserData.email.trim().toLowerCase(),
          auth0Id: auth0UserData.sub,
          nombre: auth0UserData.name || auth0UserData.given_name,
          apellido: auth0UserData.family_name || '',
          picture: auth0UserData.picture || null,
          cuentaSeleccionada: {
            tipo: cuenta.tipo,
            id: cuenta.id,
          },
        },
        { withCredentials: true, timeout: 10000 }
      );

      if (response?.data?.requiresSelection) {
        setCuentasDisponibles(response.data.cuentas || []);
        return;
      }

      await completarLoginConRespuesta(response.data);
    } catch (error) {
      const msg = error?.response?.data?.message || 'No se pudo iniciar sesión con la cuenta seleccionada';
      message.error(msg);
    } finally {
      setSeleccionandoCuenta(false);
    }
  };

  useEffect(() => {
    // Protección contra doble ejecución (React.StrictMode en desarrollo)
    if (hasProcessedRef.current) {
      console.log('⚠️ [Auth0Callback] useEffect ya fue procesado, ignorando segunda ejecución');
      return;
    }
    hasProcessedRef.current = true;
    
    console.log('🔍 [Auth0Callback] useEffect ejecutado');
    
    const handleCallback = async () => {
      try {
        console.log('🔍 [Auth0Callback] handleCallback iniciado');
        setDebugInfo({ step: '1. Leyendo código de Auth0...', data: null, error: null });
        
        // Auth0 redirige a /auth0-callback?code=xxx (sin hash cuando usamos HashRouter)
        // Necesitamos leer el código de los query params normales primero
        
        const urlInfo = {
          href: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        };
        
        setDebugInfo(prev => ({ 
          ...prev, 
          step: '1. URL recibida',
          data: { urlInfo }
        }));
        
        // Log completo de la URL para debug
        console.log('🔍 URL completa:', window.location.href);
        console.log('🔍 window.location.pathname:', window.location.pathname);
        console.log('🔍 window.location.search:', window.location.search);
        console.log('🔍 window.location.hash:', window.location.hash);
      
      // PRIMERO: Leer de window.location.search (query params normales)
      // Esto es lo que Auth0 usa cuando redirige sin hash
      const urlParams = new URLSearchParams(window.location.search);
      let code = urlParams.get('code');
      let error = urlParams.get('error');
      
      console.log('🔍 [1] Código de window.location.search:', code);
      console.log('🔍 [1] Error de window.location.search:', error);
      
      // SEGUNDO: Si no está en query params normales, intentar desde searchParams (HashRouter)
      if (!code && !error) {
        code = searchParams.get('code');
        error = searchParams.get('error');
        console.log('🔍 [2] Código de searchParams:', code);
        console.log('🔍 [2] Error de searchParams:', error);
      }
      
      // TERCERO: Si no está en query params, intentar leer del hash
      // El hash puede ser: #/auth0-callback?code=xxx o #code=xxx
      if (!code && !error) {
        const hash = window.location.hash;
        console.log('🔍 [3] Hash completo:', hash);
        
        if (hash) {
          // Caso 1: Hash con query params después de la ruta: #/auth0-callback?code=xxx
          const hashWithQuery = hash.match(/\?([^#]+)/);
          if (hashWithQuery) {
            const hashParams = new URLSearchParams(hashWithQuery[1]);
            code = code || hashParams.get('code');
            error = error || hashParams.get('error');
            console.log('🔍 [3a] Código del hash (query params):', code);
          }
          
          // Caso 2: Hash con fragment: #code=xxx
          if (!code && !error && hash.includes('code=')) {
            const hashPart = hash.substring(1); // Remover el # inicial
            const hashParams = new URLSearchParams(hashPart);
            code = code || hashParams.get('code');
            error = error || hashParams.get('error');
            console.log('🔍 [3b] Código del hash (directo):', code);
          }
        }
      }

      if (error) {
        console.error('❌ Error de Auth0:', error);
        setDebugInfo({ 
          step: 'ERROR: Error de Auth0', 
          data: { error }, 
          error: `Error en autenticación: ${error}` 
        });
        message.error(`Error en autenticación: ${error}`);
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      if (!code) {
        console.error('❌ No se recibió código de autorización de Auth0');
        console.error('❌ URL completa:', window.location.href);
        setDebugInfo({ 
          step: 'ERROR: No se recibió código', 
          data: { urlInfo }, 
          error: 'No se recibió código de autorización de Auth0' 
        });
        message.error('No se recibió código de autorización. Por favor, intenta nuevamente.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }
      
      console.log('✅ Código recibido de Auth0:', code);
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '2. Código recibido de Auth0',
        data: { ...prev.data, code: code.substring(0, 20) + '...' }
      }));

      // Continuar con el intercambio de código por token
      setDebugInfo(prev => ({ ...prev, step: '3. Intercambiando código por token con Auth0...' }));
      
      // Intercambiar código por token con Auth0
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
        const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
        const auth0RedirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin;

      setDebugInfo(prev => ({ 
        ...prev, 
        step: '3. Configuración de Auth0',
        data: { 
          ...prev.data, 
          auth0Config: {
            domain: auth0Domain ? 'Configurado' : 'NO CONFIGURADO',
            clientId: auth0ClientId ? 'Configurado' : 'NO CONFIGURADO',
            redirectUri: auth0RedirectUri,
          }
        }
      }));

      if (!auth0Domain || !auth0ClientId) {
        setDebugInfo({ 
          step: 'ERROR: Auth0 no configurado', 
          data: { auth0Domain, auth0ClientId }, 
          error: 'Auth0 no está configurado' 
        });
        message.error('Auth0 no está configurado');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      // El redirect_uri debe ser el mismo que se usó en la autorización (sin hash)
      // Auth0 redirige a http://localhost:3001?code=xxx y luego Auth0RootHandler lo maneja
      const redirectUri = auth0RedirectUri; // http://localhost:3001 (sin hash)
      
      console.log('🔍 Redirect URI para intercambio de token:', redirectUri);
      
      let tokenResponse;
      let userInfo;
      
      try {
        // Intercambiar código por token
        tokenResponse = await axios.post(
            `https://${auth0Domain}/oauth/token`,
            {
              grant_type: 'authorization_code',
              client_id: auth0ClientId,
              code: code,
              redirect_uri: redirectUri,
            },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        // Validar que la respuesta tenga datos
        if (!tokenResponse || !tokenResponse.data) {
          console.error('❌ Respuesta de Auth0 inválida:', tokenResponse);
          setDebugInfo({ 
            step: 'ERROR: Respuesta de Auth0 inválida', 
            data: { tokenResponse }, 
            error: 'No se recibieron datos de Auth0. Por favor, intenta nuevamente.' 
          });
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        const { access_token, id_token } = tokenResponse.data;

        // Validar que los tokens estén presentes
        if (!access_token) {
          console.error('❌ No se recibió access_token de Auth0');
          setDebugInfo({ 
            step: 'ERROR: No se recibió access_token', 
            data: { tokenResponse: tokenResponse.data }, 
            error: 'No se recibió el token de acceso de Auth0. Por favor, intenta nuevamente.' 
          });
          message.error('Error al obtener el token de acceso. Por favor, intenta nuevamente.');
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Obtener información del usuario desde Auth0
        const userInfoResponse = await axios.get(
          `https://${auth0Domain}/userinfo`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        // Validar que la respuesta tenga datos
        if (!userInfoResponse || !userInfoResponse.data) {
          console.error('❌ Respuesta de userinfo de Auth0 inválida:', userInfoResponse);
          setDebugInfo({ 
            step: 'ERROR: Respuesta de userinfo inválida', 
            data: { userInfoResponse }, 
            error: 'No se recibieron datos del usuario de Auth0. Por favor, intenta nuevamente.' 
          });
          message.error('Error al obtener la información del usuario. Por favor, intenta nuevamente.');
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        userInfo = userInfoResponse.data;
      } catch (auth0Error) {
        console.error('❌ Error al comunicarse con Auth0:', auth0Error);
        const errorMessage = auth0Error.response?.data?.error_description || 
                            auth0Error.response?.data?.error || 
                            auth0Error.message || 
                            'Error al comunicarse con Auth0';
        setDebugInfo({ 
          step: 'ERROR: Error de Auth0', 
          data: { 
            error: auth0Error.response?.data,
            status: auth0Error.response?.status,
            message: auth0Error.message
          }, 
          error: errorMessage
        });
        message.error(`Error de autenticación: ${errorMessage}`);
        setTimeout(() => navigate('/login'), 5000);
        return;
      }
      
      console.log('📧 Información del usuario de Auth0:', {
        email: userInfo.email,
        sub: userInfo.sub,
        name: userInfo.name,
        given_name: userInfo.given_name,
        family_name: userInfo.family_name,
      });

      setDebugInfo(prev => ({ 
        ...prev, 
        step: '4. Información de Auth0 obtenida',
        data: { 
          ...prev.data, 
          auth0UserInfo: {
            email: userInfo.email,
            sub: userInfo.sub,
            name: userInfo.name,
            given_name: userInfo.given_name,
            family_name: userInfo.family_name,
          }
        }
      }));

      // Verificar que el email esté presente
      if (!userInfo.email) {
        console.error('❌ No se recibió email de Auth0');
        setDebugInfo({ 
          step: 'ERROR: No hay email de Auth0', 
          data: { userInfo }, 
          error: 'No se pudo obtener el email de la cuenta' 
        });
        message.error('No se pudo obtener el email de la cuenta. Por favor, verifica tu configuración de Auth0.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      // Autenticar con nuestro backend usando el email de Auth0
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '5. Enviando email al backend...',
        data: { 
          ...prev.data, 
          backendRequest: {
            email: userInfo.email.trim().toLowerCase(),
            apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
          }
        }
      }));
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const emailToSend = userInfo.email.trim().toLowerCase();
      
      console.log('🔍 Buscando alumno con email:', userInfo.email);
      console.log('🔍 API URL:', apiUrl);
      console.log('🔍 Email que se enviará:', emailToSend);
      console.log('🔍 URL completa del request:', `${apiUrl}/usuarios/auth0-login`);
      
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '5. Enviando request al backend...',
        data: { 
          ...prev.data, 
          backendRequest: {
            email: emailToSend,
            apiUrl: apiUrl,
            endpoint: `${apiUrl}/usuarios/auth0-login`,
            requestBody: {
              email: emailToSend,
              auth0Id: userInfo.sub,
              nombre: userInfo.name || userInfo.given_name,
              apellido: userInfo.family_name || '',
            }
          }
        }
      }));
      
      try {
          const requestUrl = `${apiUrl}/usuarios/auth0-login`;
          const requestBody = {
            email: userInfo.email.trim().toLowerCase(), // Normalizar el email
            auth0Id: userInfo.sub,
            nombre: userInfo.name || userInfo.given_name,
            apellido: userInfo.family_name || '',
            picture: userInfo.picture || null, // Incluir la foto de Auth0
          };
          
          console.log('🚀 [FRONTEND] Enviando request al backend:');
          console.log('   URL:', requestUrl);
          console.log('   Body:', JSON.stringify(requestBody, null, 2));
          console.log('   Timestamp:', new Date().toISOString());
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '5. Enviando request al backend (axios.post)...',
            data: { 
              ...prev.data, 
              backendRequest: {
                ...prev.data.backendRequest,
                timestamp: new Date().toISOString(),
                axiosConfig: {
                  url: requestUrl,
                  method: 'POST',
                  withCredentials: true,
                }
              }
            }
          }));
          
          const backendResponse = await axios.post(
            requestUrl,
            requestBody,
            { 
              withCredentials: true,
              timeout: 10000, // 10 segundos de timeout
            }
          );
          
          console.log('✅ [FRONTEND] Respuesta recibida del backend');
          console.log('   Status:', backendResponse.status);
          console.log('   Headers:', backendResponse.headers);
          
          console.log('✅ Respuesta del backend recibida');
          console.log('📦 Respuesta completa:', JSON.stringify(backendResponse.data, null, 2));

          if (backendResponse?.data?.requiresSelection) {
            setAuth0UserData(userInfo);
            setCuentasDisponibles(Array.isArray(backendResponse.data.cuentas) ? backendResponse.data.cuentas : []);
            setIsProcessing(false);
            sessionStorage.removeItem('auth0Processing');
            return;
          }

          await completarLoginConRespuesta(backendResponse.data);
        } catch (backendError) {
          setIsProcessing(false); // Marcar que el procesamiento terminó (con error)
          sessionStorage.removeItem('auth0Processing'); // Limpiar el flag de procesamiento
          console.error('❌ Error al autenticar con backend:', backendError);
          console.error('❌ Tipo de error:', backendError.name);
          console.error('❌ Mensaje de error:', backendError.message);
          console.error('❌ Email buscado:', userInfo.email);
          console.error('❌ URL intentada:', `${apiUrl}/usuarios/auth0-login`);
          console.error('❌ Status code:', backendError.response?.status);
          console.error('❌ Response data:', backendError.response?.data);
          console.error('❌ Request config:', backendError.config);
          
          const errorData = {
            name: backendError.name,
            message: backendError.message,
            status: backendError.response?.status,
            statusText: backendError.response?.statusText,
            responseMessage: backendError.response?.data?.message,
            responseData: backendError.response?.data,
            emailBuscado: userInfo.email,
            urlIntentada: `${apiUrl}/usuarios/auth0-login`,
            code: backendError.code, // Para errores de red (ECONNREFUSED, etc.)
          };
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '❌ ERROR: Error al autenticar con backend',
            data: { 
              ...prev.data, 
              backendError: errorData
            },
            error: backendError.response?.data?.message || backendError.message || 'Error al autenticar con el sistema'
          }));
          
          // Si el usuario no existe, redirigir al registro con los datos de Auth0
          if (backendError.response?.status === 404) {
            console.log('ℹ️ Usuario no encontrado, redirigiendo a registro con datos de Auth0');
            sessionStorage.removeItem('auth0Processing');
            navigate('/registro', {
              state: {
                auth0Data: {
                  email: userInfo.email.trim().toLowerCase(),
                  nombre: userInfo.given_name || userInfo.name || '',
                  apellido: userInfo.family_name || '',
                  auth0Id: userInfo.sub,
                  picture: userInfo.picture || null,
                },
              },
              replace: true,
            });
            return;
          } else if (backendError.code === 'ECONNREFUSED' || backendError.message.includes('Network Error')) {
            const errorMsg = `No se pudo conectar al servidor. Verifica que el backend esté corriendo en ${apiUrl}`;
            console.error('❌ Error de conexión:', errorMsg);
            message.error(errorMsg);
            setTimeout(() => navigate('/login'), 5000);
          } else {
            const errorMsg = backendError.response?.data?.message || backendError.message || 'Error al autenticar con el sistema';
            console.error('❌ Error del backend:', errorMsg);
            message.error(errorMsg);
            setTimeout(() => navigate('/login'), 5000);
          }
        }
      } catch (error) {
        setIsProcessing(false); // Marcar que el procesamiento terminó (con error)
        sessionStorage.removeItem('auth0Processing'); // Limpiar el flag de procesamiento
        console.error('═══════════════════════════════════════════════════════════');
        console.error('❌ [Auth0Callback] ERROR GENERAL EN handleCallback');
        console.error('   Error:', error);
        console.error('   Error message:', error.message);
        console.error('   Error stack:', error.stack);
        console.error('   Timestamp:', new Date().toISOString());
        console.error('═══════════════════════════════════════════════════════════');
        
        setDebugInfo(prev => ({ 
          ...prev, 
          step: '❌ ERROR GENERAL',
          error: error.message || 'Error desconocido',
          data: { 
            ...prev.data, 
            errorDetails: {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          }
        }));
        
        message.error('Error al procesar la autenticación: ' + (error.message || 'Error desconocido'));
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleCallback().catch(err => {
      console.error('❌ [Auth0Callback] Error no capturado en handleCallback:', err);
    });
  }, [searchParams, login, navigate]);

  if (!isProcessing && cuentasDisponibles.length > 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#1a1a1a',
          padding: 16,
        }}
      >
        <Card
          title="Elegí con qué cuenta ingresar"
          style={{ width: '100%', maxWidth: 620, background: '#2d2d2d', borderColor: '#404040' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {cuentasDisponibles.map((cuenta) => (
              <Button
                key={`${cuenta.tipo}-${cuenta.id}`}
                block
                onClick={() => handleSeleccionarCuenta(cuenta)}
                loading={seleccionandoCuenta}
                style={{ height: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Tag color={cuenta.tipo === 'JUGADOR' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
                    {cuenta.tipo}
                  </Tag>
                  <span>{cuenta.nombre || cuenta.email}</span>
                </span>
                <span style={{ opacity: 0.8 }}>{cuenta.categoria || cuenta.email}</span>
              </Button>
            ))}
            <Button type="link" onClick={() => navigate('/login')}>
              Volver al login
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // Si hay un error Y el procesamiento terminó (no estamos en proceso), mostrar mensaje de error
  // Esto evita mostrar errores temporales durante el proceso de autenticación
  if (debugInfo.error && !isProcessing) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#1a1a1a',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Text type="danger" style={{ fontSize: '16px' }}>{debugInfo.error}</Text>
        <Button type="primary" onClick={() => navigate('/login')}>
          Volver al Login
        </Button>
      </div>
    );
  }

  // Mostrar loader simple mientras procesa
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#1a1a1a',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Spin size="large" tip="Procesando autenticación..." />
      <Text style={{ color: '#e0e0e0' }}>Por favor, espera...</Text>
    </div>
  );
};

export default Auth0Callback;

